import type { CSSProperties, FormEvent } from 'react'
import type { EnergyType } from 'types'
import type { OfferInputName, Offer } from 'types/seller'

import { useState, useEffect } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import InputMask from 'react-input-mask'

import { useStore } from 'store'
import { ENERGY_TYPE, fiveYearsFromNow } from 'common'
import { adminPanelPaths, clientPanelPaths, basePaths } from 'paths'
import {
  addParamToPath,
  dimmBackground,
  getInput,
  getValue,
  leaveOnlyNumbers,
  trimFormData
} from 'utils'
import copy from 'copy.json'
import { addOfferFormAutoFill } from 'utils/devTools'
import { validFileExtension, validFileExtensions } from 'utils/validators'
import { validateOfferForm } from 'utils/pageValidators'

import useIsClientPage from 'hooks/useIsClientPage'

import Button from 'components/Button'
import InfoTooltip from 'components/InfoTooltip'
import Input, { PrefixedInput } from 'components/Input'
import InputLabel from 'components/InputLabel'
import Radio, { ActiveRadioLabel, CombinedRadios } from 'components/Radio'
import Spinner from 'components/Spinner'
import AddResourceContent from 'components/AddResourceContent'
import Checkbox, { CheckboxLabel } from 'components/Checkbox'
import CsvFormatGuide from 'components/CsvFormatGuide'
import { energyProfileCsvGuide } from 'components/csvFormatGuides'

import { SunIcon, WaterIcon, WindIcon } from 'icons'

const text = copy.seller.add_offer
const form_text = copy.common.form

export default function AddBuiltOfferScreen() {
  const { api, currentUser, fieldErrors } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isClientPage = useIsClientPage()

  const [energyType, setEnergyType] = useState<EnergyType>('SOLAR')
  const [loading, setLoading] = useState(false)
  const [profileFile, setProfileFile] = useState<File | null>(null)
  const [noProfileFileChecked, setNoProfileFileChecked] = useState(false)
  const [errors, setErrors] = useState<{ [key in OfferInputName]?: string }>({})

  const electricEnergyMeterNumber = location.state.electricEnergyMeterNumber as string
  const sellerId = location.state.sellerId

  const validateInput = (
    event?: FormEvent<HTMLInputElement>,
    element?: HTMLInputElement | null
  ) => {
    const input = element || event?.currentTarget

    if (!input) return

    const validation = validateOfferForm(input)

    setErrors(state => ({ ...state, [input.name]: validation }))

    return validation
  }

  const isFileValid = (file: File) => {
    if (validFileExtension(file.name) !== undefined) {
      setErrors(state => ({ ...state, profileFile: copy.validators.file_extension }))
      return false
    }

    if (file.size > 25000000) {
      setErrors(state => ({ ...state, profileFile: copy.validators.file_too_big }))
      return false
    }

    return true
  }

  const handleProfileFileChange = (event: FormEvent<HTMLInputElement>) => {
    validateInput(event)

    const currentTarget = event.currentTarget
    const file = currentTarget.files?.[0]

    if (!file || !isFileValid(file)) return

    setProfileFile(file)
  }

  const getCity = async (zipCode: string) => {
    const input: HTMLInputElement | null = document.querySelector('[name="location_city"]')
    const city = await api.getCity(zipCode, isClientPage)

    if (!input || !city) return

    input.value = city
    setErrors(state => ({ ...state, location_city: undefined }))
  }

  const onProfileFileCheckboxChange = (event: FormEvent<HTMLInputElement>) => {
    setNoProfileFileChecked(event.currentTarget.checked)
    if (event.currentTarget.checked) {
      setProfileFile(null)
    }
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (loading || !(currentUser || isClientPage)) return

    const form = event.currentTarget
    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateOfferForm(input) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) {
      setErrors(errors)
      return
    }

    setLoading(true)

    const requestData: Partial<Offer> = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({ ...result, [name]: value === '' ? null : value }),
      {}
    )

    if (currentUser) {
      requestData.seller_id = currentUser.id
    }

    requestData.installation_power = parseFloat(String(requestData.installation_power))
    requestData.yearly_power_production = parseFloat(String(requestData.yearly_power_production))
    requestData.minimal_contract_length = parseFloat(String(requestData.minimal_contract_length))
    requestData.maximal_contract_length = parseFloat(String(requestData.maximal_contract_length))
    requestData.energy_multiplier = parseFloat(String(requestData.energy_multiplier))
    requestData.energy_price = parseFloat(String(requestData.energy_price))
    requestData.location_zip_code = leaveOnlyNumbers(requestData.location_zip_code)
    requestData.electric_energy_meter_number = electricEnergyMeterNumber

    // @ts-ignore
    delete requestData.profileFile

    const formData = new FormData()

    formData.append(
      'request',
      new Blob([JSON.stringify(requestData)], { type: 'application/json' })
    )

    if (profileFile) formData.append('profileFile', profileFile)

    const response = await api.addBuiltOffer(isClientPage, formData)

    setLoading(false)

    if (response && sellerId) {
      navigate(addParamToPath(adminPanelPaths.seller.search, sellerId))
      return
    }

    if (response && isClientPage) {
      navigate(clientPanelPaths.confirmation)
      return
    }

    if (response) navigate(adminPanelPaths.seller.search)
  }

  useEffect(dimmBackground)

  useEffect(() => {
    setErrors(fieldErrors)
    getInput(Object.keys(fieldErrors)[0])?.scrollIntoView({ behavior: 'smooth' })
  }, [fieldErrors])

  useEffect(() => {
    if (errors.profileFile) {
      setProfileFile(null)
      const fileInput = document.querySelector<HTMLInputElement>('[name="profileFile"]')
      if (fileInput) fileInput.value = ''
    }
  }, [errors])

  if (!currentUser && !isClientPage) return <Navigate to={basePaths.admin_panel} />

  return (
    <AddResourceContent as="form" noValidate onSubmit={submit}>
      {process.env.NODE_ENV === 'development' && (
        <button type="button" onClick={addOfferFormAutoFill}>
          Auto fill
        </button>
      )}

      <h1>{text.heading}</h1>
      <p>{text.paragraph}</p>

      <ElectricEnergyMeterNumberWrapper>
        <InputLabel>
          <span>{form_text.electric_energy_meter_number}</span>
          <span>{errors.electric_energy_meter_number}</span>
          <Input
            name="electric_energy_meter_number"
            disabled
            value={electricEnergyMeterNumber}
            onBlur={validateInput}
          />
        </InputLabel>

        <Link
          to={
            isClientPage
              ? clientPanelPaths.additional_information
              : adminPanelPaths.seller.additional_information
          }
        >
          {text.link}
        </Link>
      </ElectricEnergyMeterNumberWrapper>

      <CombinedRadios>
        <span>{form_text.energy_type}</span>
        <div>
          <ActiveRadioLabel active={energyType === 'SOLAR'} hiddenRadio style={{ padding: 10 }}>
            <Radio
              name="energy_type"
              value="SOLAR"
              checked={energyType === 'SOLAR'}
              onChange={() => setEnergyType('SOLAR')}
            />
            <SunIcon /> {ENERGY_TYPE.SOLAR}
          </ActiveRadioLabel>

          <ActiveRadioLabel active={energyType === 'HYDRO'} hiddenRadio style={{ padding: 10 }}>
            <Radio
              name="energy_type"
              value="HYDRO"
              checked={energyType === 'HYDRO'}
              onChange={() => setEnergyType('HYDRO')}
            />
            <WaterIcon /> {ENERGY_TYPE.HYDRO}
          </ActiveRadioLabel>

          <ActiveRadioLabel active={energyType === 'WIND'} hiddenRadio style={{ padding: 10 }}>
            <Radio
              name="energy_type"
              value="WIND"
              checked={energyType === 'WIND'}
              onChange={() => setEnergyType('WIND')}
            />
            <WindIcon /> {ENERGY_TYPE.WIND}
          </ActiveRadioLabel>
        </div>
      </CombinedRadios>

      <InputLabel>
        <span>{form_text.osd_name}</span>
        <span>{errors.osd_name}</span>
        <Input name="osd_name" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span>{form_text.energy_point_number}</span>
        <span>{errors.energy_point_number}</span>
        <Input name="energy_point_number" onBlur={validateInput} />
      </InputLabel>

      <InputLabel>
        <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {form_text.energy_multiplier}
          <InfoTooltip>{form_text.energy_multiplier_hint}</InfoTooltip>
        </span>
        <span>{errors.energy_multiplier}</span>
        <Input
          name="energy_multiplier"
          type="number"
          step="1"
          min="1"
          max="1000000"
          defaultValue="1"
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{form_text.installation_power}</span>
        <span>{errors.installation_power}</span>
        <PrefixedInput
          name="installation_power"
          type="number"
          prefix={copy.common.MW}
          prefixWidth="50px"
          reverse
          step={0.01}
          min={0}
          max={1000000}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{text.form.yearly_power_production}</span>
        <span>{errors.yearly_power_production}</span>
        <PrefixedInput
          name="yearly_power_production"
          type="number"
          prefix={copy.common.MWh}
          prefixWidth="50px"
          reverse
          step={1}
          min={1}
          max={1000000}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{form_text.energy_price}</span>
        <span>{errors.energy_price}</span>
        <PrefixedInput
          name="energy_price"
          prefix={copy.common.zl}
          prefixWidth="28px"
          reverse
          type="number"
          step={0.01}
          min={1}
          max={10000}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <InputLabel>
        <span>{text.form.production_start_date}</span>
        <span>{errors.production_start_date}</span>
        <Input
          type="date"
          name="production_start_date"
          min="1970-01-01"
          max={fiveYearsFromNow}
          onBlur={validateInput}
          style={{ width: '50%' }}
        />
      </InputLabel>

      <Subheader>{text.subheading_1}</Subheader>

      <InputLabel>
        <span>{form_text.location_zip_code}</span>
        <span>{errors.location_zip_code}</span>
        <InputMask
          mask="99-999"
          maskPlaceholder={null}
          onInput={event => !validateInput(event) && getCity(event.currentTarget.value)}
          onBlur={validateInput}
        >
          <Input name="location_zip_code" style={{ width: '50%' }} />
        </InputMask>
      </InputLabel>

      <InputLabel>
        <span>{form_text.location_city}</span>
        <span>{errors.location_city}</span>
        <Input name="location_city" onBlur={validateInput} />
      </InputLabel>

      <Subheader>{text.subheading_2}</Subheader>

      <div style={{ display: 'flex' }}>
        <InputLabel style={{ width: '100%' }}>
          <span>{text.form.minimal_contract_length}</span>
          <span style={{ '--errorMessageHeight': '45px' } as CSSProperties}>
            {errors.minimal_contract_length}
          </span>
          <PrefixedInput
            name="minimal_contract_length"
            type="number"
            prefix={copy.common.months_abbr}
            prefixWidth="52px"
            reverse
            min={1}
            max={240}
            onBlur={event => {
              validateInput(event)
              getValue('maximal_contract_length') &&
                validateInput(undefined, getInput('maximal_contract_length'))
            }}
          />
        </InputLabel>

        <InputLabel style={{ width: '100%' }}>
          <span>{text.form.maximal_contract_length}</span>
          <span style={{ '--errorMessageHeight': '45px' } as CSSProperties}>
            {errors.maximal_contract_length}
          </span>
          <PrefixedInput
            name="maximal_contract_length"
            type="number"
            prefix={copy.common.months_abbr}
            prefixWidth="52px"
            reverse
            min={1}
            max={240}
            onBlur={event => {
              validateInput(event)
              getValue('minimal_contract_length') &&
                validateInput(undefined, getInput('minimal_contract_length'))
            }}
          />
        </InputLabel>
      </div>
      <div>
        <TooltipWrapper>
          {text.form.profile_file}
          <InfoTooltip>{text.form.profile_file_hint}</InfoTooltip>
        </TooltipWrapper>

        {!noProfileFileChecked && (
          <InputLabel hideErrorOnFocus={false}>
            <span /> {/* Empty span for correct errors styling */}
            <span>{errors.profileFile}</span>
            <Input
              name="profileFile"
              type="file"
              accept={validFileExtensions.join(',')}
              onBlur={event =>
                document.activeElement !== event.currentTarget && validateInput(event)
              }
              onChange={handleProfileFileChange}
            />
            <CsvFormatGuide definition={energyProfileCsvGuide} />
          </InputLabel>
        )}

        <CheckboxLabelStyled>
          <Checkbox checked={noProfileFileChecked} onChange={onProfileFileCheckboxChange} />
          {form_text.no_profile_file}
        </CheckboxLabelStyled>
      </div>

      <Button type="submit">{loading ? <Spinner size={16} /> : text.button}</Button>

      <Button secondary onClick={() => navigate(-1)} type="button">
        {copy.common.button.go_back}
      </Button>
    </AddResourceContent>
  )
}

const ElectricEnergyMeterNumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  > a {
    text-decoration: none;
    color: var(--green);
  }
`

const Subheader = styled.h4`
  font: var(--600-16);
`

const CheckboxLabelStyled = styled(CheckboxLabel)`
  color: var(--gray);
  margin: 10px 0 10px 0;

  ${Checkbox} {
    width: 24px;
    height: 24px;
    border-color: var(--light-gray);
  }
`

const TooltipWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--gray);
`
